import * as React from "react"
import { useRender } from "@base-ui/react/use-render"

import { resolveAsChild } from "./as-child"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-full border border-transparent px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-colors overflow-hidden",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary-strong underline-offset-4 [a&]:hover:underline",
        // Status pills: a dot and a word, never colour alone. Tone means attention.
        positive: "h-6 gap-1.5 px-2.5 bg-status-positive-subtle text-status-positive before:size-1.5 before:rounded-full before:bg-current",
        warning: "h-6 gap-1.5 px-2.5 bg-status-warning-subtle text-status-warning before:size-1.5 before:rounded-full before:bg-current",
        critical: "h-6 gap-1.5 px-2.5 bg-status-critical-subtle text-status-critical before:size-1.5 before:rounded-full before:bg-current",
        info: "h-6 gap-1.5 px-2.5 bg-status-info-subtle text-status-info before:size-1.5 before:rounded-full before:bg-current",
        neutral: "h-6 gap-1.5 px-2.5 bg-status-neutral-subtle text-status-neutral before:size-1.5 before:rounded-full before:bg-current",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  asChild = false,
  render,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean
    render?: useRender.RenderProp
  }) {
  const resolved = resolveAsChild(asChild, render, props)

  return useRender({
    defaultTagName: "span",
    render: resolved.render,
    props: {
      "data-slot": "badge",
      "data-variant": variant,
      className: cn(badgeVariants({ variant }), className),
      ...resolved.props,
    },
  })
}

export { Badge };
